import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { fromEvent, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const UNSTABLE_CLEAR_MS = 30_000;

export function isNetworkHttpError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    return error.status === 0;
  }
  return false;
}

@Injectable({ providedIn: 'root' })
export class NetworkConnectivityService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly browserOnline = signal(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  private readonly requestUnstable = signal(false);
  private unstableClearTimer?: ReturnType<typeof setTimeout>;

  readonly isOffline = computed(() => !this.browserOnline());
  readonly isUnstable = computed(
    () => this.browserOnline() && this.requestUnstable()
  );
  readonly showBanner = computed(() => this.isOffline() || this.isUnstable());

  readonly bannerMessage = computed(() => {
    if (this.isOffline()) {
      return "You're offline. Changes may not save until your connection is restored.";
    }
    return 'Connection is unstable. Some actions may fail—check your network and try again.';
  });

  readonly bannerSeverity = computed<'offline' | 'unstable'>(() =>
    this.isOffline() ? 'offline' : 'unstable'
  );

  initialize(): void {
    this.syncBrowserOnline();

    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((online) => {
        this.browserOnline.set(online);
        if (online) {
          this.verifyConnectivity();
        }
      });
  }

  syncBrowserOnline(): void {
    if (typeof navigator === 'undefined') {
      return;
    }
    this.browserOnline.set(navigator.onLine);
  }

  reportRequestSuccess(): void {
    this.requestUnstable.set(false);
    if (this.unstableClearTimer) {
      clearTimeout(this.unstableClearTimer);
      this.unstableClearTimer = undefined;
    }
  }

  reportRequestFailure(error: unknown): void {
    if (!isNetworkHttpError(error) || !this.browserOnline()) {
      return;
    }
    this.requestUnstable.set(true);
    if (this.unstableClearTimer) {
      clearTimeout(this.unstableClearTimer);
    }
    this.unstableClearTimer = setTimeout(() => {
      this.requestUnstable.set(false);
      this.unstableClearTimer = undefined;
    }, UNSTABLE_CLEAR_MS);
  }

  /** User-triggered connectivity check after reconnecting. */
  recheckConnection(): void {
    this.syncBrowserOnline();
    if (!navigator.onLine) {
      return;
    }
    this.verifyConnectivity();
  }

  private verifyConnectivity(): void {
    const url = `${environment.baseApiUrl}/api/Session/status`;
    fetch(url, { credentials: 'include' })
      .then((response) => {
        if (response.ok) {
          this.reportRequestSuccess();
        } else {
          this.reportRequestFailure(new HttpErrorResponse({ status: 0 }));
        }
      })
      .catch(() => {
        this.reportRequestFailure(new HttpErrorResponse({ status: 0 }));
      });
  }
}
